/**
 * Firebase Cloud Messaging — Web Push (Phase 3)
 *
 * Handles token registration (POST /api/device-tokens) and cleanup (DELETE) on logout.
 * If the user denies notification permission the inbox (Phase 2) keeps working normally.
 */
import { initializeApp, getApps, getApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, deleteToken as fbDeleteToken, isSupported } from 'firebase/messaging'
import Http from '../lib/http'
import { getBearerToken } from '../utils/sessionAuth'

const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
}

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY
export const FCM_TOKEN_KEY = 'fcm_web_token'
export const WEB_PUSH_ENABLED_EVENT = 'web-push-enabled'
const MESSAGING_SW_URL = '/firebase-messaging-sw.js'
const FOREGROUND_REFETCH_DELAY_MS = 300

const foregroundListeners = new Set()
let _messagingInstance = null
let _messagingSwRegistration = null
let _foregroundOnMessageUnsub = null

export async function isWebPushSupportedInBrowser() {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false
  try {
    return await isSupported()
  } catch {
    return false
  }
}

export function getWebPushPermission() {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

export function isWebPushEnabledLocally() {
  return Boolean(localStorage.getItem(FCM_TOKEN_KEY)) && getWebPushPermission() === 'granted'
}

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG)
}

async function attachMessagingServiceWorkerRegistration(registration) {
  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  }
  await registration.update().catch(() => {})
  await navigator.serviceWorker.ready
  _messagingSwRegistration = registration
  if (_foregroundOnMessageUnsub) {
    _foregroundOnMessageUnsub()
    _foregroundOnMessageUnsub = null
  }
  _messagingInstance = null
  return registration
}

async function registerMessagingServiceWorker() {
  const registration = await navigator.serviceWorker.register(MESSAGING_SW_URL)
  return attachMessagingServiceWorkerRegistration(registration)
}

/** Reuse an already-registered SW after reload (foreground onMessage + getToken). */
async function ensureMessagingServiceWorkerReady() {
  if (_messagingSwRegistration) return _messagingSwRegistration
  const registrations = await navigator.serviceWorker.getRegistrations()
  const registration = registrations.find((reg) => reg.active?.scriptURL?.includes('firebase-messaging-sw.js'))
  if (!registration) return null
  return attachMessagingServiceWorkerRegistration(registration)
}

async function getFirebaseMessaging() {
  if (_messagingInstance) return _messagingInstance
  const supported = await isSupported()
  if (!supported) return null
  const app = getFirebaseApp()
  if (_messagingSwRegistration) {
    try {
      _messagingInstance = getMessaging(app, { serviceWorkerRegistration: _messagingSwRegistration })
    } catch {
      _messagingInstance = getMessaging(app)
    }
  } else {
    _messagingInstance = getMessaging(app)
  }
  return _messagingInstance
}

function dispatchForegroundMessage(payload) {
  foregroundListeners.forEach((listener) => {
    try {
      listener(payload)
    } catch (_) {
      /* ignore listener errors */
    }
  })
}

async function ensureMessagingRegistrationForPush() {
  let registration = await ensureMessagingServiceWorkerReady()
  if (!registration && isWebPushEnabledLocally()) {
    registration = await registerMessagingServiceWorker()
  }
  return registration
}

async function ensureForegroundOnMessageListener() {
  if (!isWebPushEnabledLocally()) return
  await ensureMessagingRegistrationForPush()
  const messaging = await getFirebaseMessaging()
  if (!messaging) return
  if (_foregroundOnMessageUnsub) return
  _foregroundOnMessageUnsub = onMessage(messaging, (payload) => dispatchForegroundMessage(payload))
}

/** Re-wire onMessage after enable push or SW update (avoids stale listener until F5). */
export async function refreshForegroundMessageListener() {
  if (_foregroundOnMessageUnsub) {
    _foregroundOnMessageUnsub()
    _foregroundOnMessageUnsub = null
  }
  await ensureForegroundOnMessageListener()
}

export function notifyWebPushEnabled() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WEB_PUSH_ENABLED_EVENT))
  }
  refreshForegroundMessageListener().catch(() => {})
}

/**
 * Subscribes to foreground FCM messages (tab is open). Uses a single onMessage fan-out.
 * Returns a Promise that resolves to the unsubscribe function.
 */
export function subscribeToForegroundMessages(callback) {
  foregroundListeners.add(callback)
  if (isWebPushEnabledLocally()) {
    refreshForegroundMessageListener().catch(() => {})
  }
  return Promise.resolve(() => {
    foregroundListeners.delete(callback)
  })
}

export { FOREGROUND_REFETCH_DELAY_MS }

/**
 * Enables browser web push: permission + FCM token + POST /api/device-tokens.
 * @returns {{ ok: boolean, reason?: 'unsupported'|'denied'|'no_token'|'api_error'|'config_error' }}
 */
export async function enableWebPushNotifications({ forceRegister = false } = {}) {
  try {
    const supported = await isWebPushSupportedInBrowser()
    if (!supported) return { ok: false, reason: 'unsupported' }
    if (!VAPID_KEY || VAPID_KEY.includes('REPLACE')) return { ok: false, reason: 'config_error' }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { ok: false, reason: 'denied' }

    const registration = await registerMessagingServiceWorker()
    const messaging = await getFirebaseMessaging()
    if (!messaging) return { ok: false, reason: 'unsupported' }

    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration })
    if (!token) return { ok: false, reason: 'no_token' }

    const previousToken = localStorage.getItem(FCM_TOKEN_KEY)
    localStorage.setItem(FCM_TOKEN_KEY, token)
    if (forceRegister || previousToken !== token) {
      await Http.post('/api/device-tokens', { device_token: token, platform: 'web' })
    }

    notifyWebPushEnabled()
    return { ok: true }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('[FCM enable]', err)
    return { ok: false, reason: 'api_error' }
  }
}

/** After login: re-sync token only if the user already granted permission (no prompt on login). */
export async function requestAndRegisterToken() {
  if (getWebPushPermission() !== 'granted') return
  await enableWebPushNotifications({ forceRegister: false })
}

/** Turns off web push for this browser (server DELETE + Firebase deleteToken). */
export async function disableWebPushNotifications() {
  await unregisterFcmToken()
  return { ok: true }
}

/**
 * Removes the FCM token from Firebase and from the backend.
 * Call on logout so the user stops receiving push notifications.
 */
export async function unregisterFcmToken() {
  const fcmToken = localStorage.getItem(FCM_TOKEN_KEY)
  if (!fcmToken) return
  const bearer = getBearerToken()
  try {
    if (bearer) {
      await Http.delete(`/api/device-tokens/${encodeURIComponent(fcmToken)}`, {
        handlerEnabled: false,
        headers: { Authorization: `Bearer ${bearer}` },
      })
    }
    const messaging = await getFirebaseMessaging()
    if (messaging) await fbDeleteToken(messaging).catch(() => {})
  } catch (_) {
    // best-effort — always clean up localStorage
  } finally {
    localStorage.removeItem(FCM_TOKEN_KEY)
    if (_foregroundOnMessageUnsub) {
      _foregroundOnMessageUnsub()
      _foregroundOnMessageUnsub = null
    }
  }
}
